import React from 'react';
import {
  buildPagePlan,
  installPrintPageCss,
  isLandscapePrintTemplate,
  resolvePrintTemplate,
  shouldReserveStationeryBands,
  type PagePlan,
  type PlannedPage,
  type PrintDocumentModel,
} from '@/print-engine';
import { runPrintWithDocumentTitle } from '@/utils/printDocumentTitle';
import './PaginatedPrintShell.css';

export type PaginatedPrintShellProps = {
  model: PrintDocumentModel;
  plan?: PagePlan;
  diagnostics?: boolean;
  onClose?: () => void;
  /** Render one composed page body (caller supplies document-specific UI). */
  renderPage: (args: {
    page: PlannedPage;
    model: PrintDocumentModel;
    plan: PagePlan;
    totalPages: number;
  }) => React.ReactNode;
};

function usePrintDiagnosticsFlag(): boolean {
  return React.useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('printDiag') === '1';
    } catch {
      return false;
    }
  }, []);
}

/**
 * Shared stacked-page print shell for all formal documents.
 * Preview ≡ print via the same page plan.
 */
export const PaginatedPrintShell: React.FC<PaginatedPrintShellProps> = ({
  model,
  plan: planProp,
  diagnostics: diagnosticsProp,
  onClose,
  renderPage,
}) => {
  const diagFlag = usePrintDiagnosticsFlag();
  const diagnostics = diagnosticsProp ?? diagFlag;
  const template = resolvePrintTemplate(model.templateId);
  const plan = React.useMemo(
    () => planProp ?? buildPagePlan(model, { template, diagnostics: true }),
    [model, planProp, template, diagnostics]
  );

  const landscape = isLandscapePrintTemplate(template);

  React.useEffect(() => {
    const size = landscape ? 'a4 landscape' : template.pageSize === 'a4' ? 'a4' : 'letter';
    const uninstall = installPrintPageCss({ pageSize: size });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      uninstall();
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, template, landscape]);

  const sizeClass = landscape
    ? 'print-composed-page--a4-landscape'
    : template.pageSize === 'a4'
      ? 'print-composed-page--a4'
      : 'print-composed-page--letter';
  const reserveStationeryBands = shouldReserveStationeryBands(model);

  return (
    <div className="print-paginated-root" data-doc-type={model.docType}>
      {onClose ? (
        <div className="print-controls no-print">
          <button type="button" className="close-print-btn" onClick={onClose}>
            ✕ Close
          </button>
          <button
            type="button"
            className="trigger-print-btn"
            onClick={() => {
              installPrintPageCss({
                pageSize: landscape
                  ? 'a4 landscape'
                  : template.pageSize === 'a4'
                    ? 'a4'
                    : 'letter',
              });
              runPrintWithDocumentTitle(model.documentNumber);
            }}
          >
            🖨️ Print
          </button>
          {diagnostics && plan.diagnostics ? (
            <div className="print-diag-summary">
              {plan.totalPages} page(s) · usable {Math.round(plan.diagnostics.usableHeightPx)}px
              {plan.diagnostics.warnings.length
                ? ` · ${plan.diagnostics.warnings.length} warning(s)`
                : ''}
            </div>
          ) : null}
        </div>
      ) : null}

      {plan.pages.map((page) => (
        <section
          key={page.pageIndex}
          className={`print-composed-page ${sizeClass} print-composed-page--${page.type.toLowerCase()}`}
          data-page={page.pageIndex + 1}
          data-page-type={page.type}
        >
          {model.watermark?.text ? (
            <div className="print-page-watermark" aria-hidden>
              <span>{model.watermark.text}</span>
            </div>
          ) : null}
          {model.letterhead ? (
            <img
              className="print-letterhead print-letterhead-header"
              src={model.letterhead.headerSrc}
              alt=""
            />
          ) : reserveStationeryBands ? (
            <div
              className="print-letterhead-spacer print-letterhead-spacer--header"
              style={{ height: `${template.headerHeightIn}in` }}
              aria-hidden
            />
          ) : null}

          <div className="print-composed-body">
            {page.sectionIds.includes('continuationChrome') ? (
              <div className="print-continuation-chrome">
                {model.continuationLabel} | Page {page.pageIndex + 1} of {plan.totalPages}
              </div>
            ) : null}

            {renderPage({ page, model, plan, totalPages: plan.totalPages })}

            <div className="print-page-number">
              Page {page.pageIndex + 1} of {plan.totalPages}
            </div>

            {diagnostics ? (
              <div className="print-page-diag no-print">
                {page.type} · used {Math.round(page.usedHeightPx)}px · unused{' '}
                {Math.round(page.unusedHeightPx)}px · rows {page.itemRows.length}
              </div>
            ) : null}
          </div>

          {model.letterhead ? (
            <img
              className="print-letterhead print-letterhead-footer"
              src={model.letterhead.footerSrc}
              alt=""
            />
          ) : reserveStationeryBands ? (
            <div
              className="print-letterhead-spacer print-letterhead-spacer--footer"
              style={{
                height: `${template.footerHeightIn + (template.printSafeInsetBottomIn ?? 0)}in`,
              }}
              aria-hidden
            />
          ) : null}
        </section>
      ))}
    </div>
  );
};
